import Image, { type ImageLoaderProps, type ImageProps } from 'next/image';
import { AiBadge, type TFunction } from '@pkgs/components';
import { ImageAiType } from '@pkgs/api';
import { type StoryblokAssetProps } from '@ev-tech/ui-core-components/cms';

import { ImageWrapper, Placeholder } from '../../property-expose/Placeholder';

import styles from './imageResolver.module.css';

type ImageResolverOptions = Pick<ImageProps, 'width' | 'height' | 'sizes' | 'priority' | 'style' | 'fill'>;

type ImageResolverParams = {
  image: StoryblokAssetProps;
  t: TFunction;
  options: ImageResolverOptions;
};

const STORYBLOK_LIMIT = 16000000;
const MAX_IMAGE_DIMENSION = 4000;

const getFormattedImageSrc = (image: Pick<StoryblokAssetProps, 'focus' | 'filename'>, opt: Pick<ImageProps, 'width' | 'height'>) => {
  if (image.focus) {
    return `${image.filename}/m/${opt.width}x${opt.height}/filters:focal(${image.focus})`;
  }

  return `${image.filename}/m/${opt.width}x${opt.height}`;
};

const imageLoader = ({ src }: ImageLoaderProps) => {
  return src;
};

const imageResolver = ({ image, t, options: { width, height, priority, sizes = '100vw', ...otherOpts } }: ImageResolverParams) => {
  if (!image.filename) return;

  const isAiGenerated = image.meta_data?.['AI-generated'] === 'Yes';

  const resolveDimensions = (width: number, height: number) => {
    const aspectRatio = width / height;

    if (width * height > STORYBLOK_LIMIT) {
      if (width > height) {
        height = Math.round(MAX_IMAGE_DIMENSION / aspectRatio);
        width = MAX_IMAGE_DIMENSION;
      } else {
        width = Math.round(MAX_IMAGE_DIMENSION * aspectRatio);
        height = MAX_IMAGE_DIMENSION;
      }
    }

    return { width, height };
  };

  const { alt } = image;

  const resolvedDimensions = resolveDimensions(Number(width) || 0, Number(height) || 0);

  const src = getFormattedImageSrc(image, {
    width: resolvedDimensions.width,
    height: resolvedDimensions.height,
  });

  const imageElement = isAiGenerated ? (
    <div className={styles.relativeWrapper}>
      <AiBadge aiType={ImageAiType.GENERATED} t={t} translationKey="search.expose.photoGallery.label.ai" offset="xs" size="m" />
      <Image
        src={src}
        alt={alt || ''}
        sizes={sizes}
        width={!otherOpts.fill ? resolvedDimensions.width : undefined}
        height={!otherOpts.fill ? resolvedDimensions.height : undefined}
        priority={priority}
        loader={imageLoader}
        {...otherOpts}
      />
    </div>
  ) : (
    <Image
      src={src}
      alt={alt || ''}
      sizes={sizes}
      width={!otherOpts.fill ? resolvedDimensions.width : undefined}
      height={!otherOpts.fill ? resolvedDimensions.height : undefined}
      priority={priority}
      loader={imageLoader}
      {...otherOpts}
    />
  );

  // Render without Placeholder when priority is true, otherwise wrap with Placeholder
  return priority ? <ImageWrapper>{imageElement}</ImageWrapper> : <Placeholder>{imageElement}</Placeholder>;
};

export default imageResolver;
